/**
 * Guía de la IA de autoría y del asistente de la academia (B8).
 *
 * Cubre los `docsSlug` de las cinco capacidades nuevas: `autoria-ia` (crear,
 * editar y configurar cursos con IA) y `asistente-academia` (el chatbot que
 * atiende a quien todavía no es alumno).
 */
import { msg } from "@lingui/core/macro";
import { Wand2 } from "lucide-solid";

import type { HelpGuide } from "../types";
import { Bullets, GoTo, Note, Steps } from "../components/help-prose";

export const aiAuthoringGuide: HelpGuide = {
  key: "help.ai_authoring",
  slug: "autoria-ia",
  title: msg`AI authoring and academy assistant`,
  description: msg`Turn a brief into a course proposal you approve piece by piece, and let an assistant answer prices and schedules to people who are not students yet.`,
  audience: msg`Administrators and course authors`,
  icon: Wand2,
  sections: [
    {
      id: "generar",
      title: msg`Generate a course from a brief`,
      summary: msg`Describe the training in one line and get a proposal: program, course, units and one module per class.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Open a course in the editor and press "AI".`,
              msg`Describe the training, for example: "Digital marketing, level 1, 3 months, 2 classes per week".`,
              msg`Review the proposal piece by piece and approve only what convinces you.`,
            ]}
          />
          <Note
            msg={msg`The AI never writes to your courses. It writes a proposal; applying it is a separate step that you take.`}
          />
        </>
      ),
    },
    {
      id: "revisar",
      title: msg`Review and approve piece by piece`,
      summary: msg`Every piece is checked against the real rules of the platform. What does not pass cannot be approved — the checkbox is disabled, not just flagged.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Each exercise is validated by the rules of its own block type, and each module by the settings of its own kind.`,
              msg`A module kind you turned off for the course cannot be approved, even if the AI proposed it.`,
              msg`A deletion proposed by the AI always needs a person to confirm it.`,
              msg`Applying uses the same actions as editing by hand, so the activity feed, the trash and undo work exactly as always.`,
            ]}
          />
          <Note
            msg={msg`If your AI provider does not guarantee the output format, the review screen warns you: read those proposals more carefully.`}
          />
        </>
      ),
    },
    {
      id: "editar",
      title: msg`Suggest changes to a course you already have`,
      summary: msg`The AI reads the current structure and proposes the minimum set of changes, each with a reason you can read.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`It can only touch parts of this course: anything else is rejected automatically.`,
              msg`Every change comes with one sentence explaining why, so you can decide without opening it.`,
              msg`This has its own switch: your academy can allow generating but not editing.`,
            ]}
          />
        </>
      ),
    },
    {
      id: "asistente",
      title: msg`Assistant for people who are not students yet`,
      summary: msg`Answers prices, schedules, ways to pay, requirements and certificates using your FAQ and your live catalog — and hands over to a person when it is not sure.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`It only gives information: it does not enroll anyone, does not take payments and does not book appointments.`,
              msg`It answers only with what your knowledge base says, and shows its sources. With no source it does not guess: it offers WhatsApp.`,
              msg`When it hands over, it can create a lead in the CRM with the whole conversation, so nobody starts from scratch.`,
              msg`Prices come from your catalog live, so it never quotes last month's price.`,
            ]}
          />
          <GoTo to="/admin/academy-bot" label={msg`Open the assistant panel`} />
        </>
      ),
    },
    {
      id: "coste",
      title: msg`Cost and switches`,
      summary: msg`Every AI capability has its own switch and its own monthly budget, so you can turn one off without losing the others.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Generating, editing, configuring and the public assistant are four separate switches. All start off.`,
              msg`Each one can have its own model and its own monthly token cap in the AI integration.`,
              msg`Loading a whole catalog is the expensive part: raise the authoring cap while you do it and lower it afterwards.`,
            ]}
          />
          <Note
            msg={msg`Nothing that affects a grade or a certificate should run on a cheap model. Rubrics and grading schemes are the ones to keep on your best model.`}
          />
        </>
      ),
    },
  ],
};
